import { areObjectIdsEqual } from 'helpers/idComparison';
import { findVersionById } from 'shared/domain/documentation/currentVersion';
import { findDisplayedVersion } from 'shared/domain/documentation/documentationVersionHelpers';
import {
  DocumentationViewItem,
  isFileItemType,
} from 'store/documentation/model/types';
import type { DocumentationModel, VersionModel } from 'shared/domain/documentation/documentationModel';
import { userWithPersonalDataToOptionalLabelled } from 'shared/domain/user/mapping/toView';
import { stringToViewDate } from 'shared/utils/date/stringToViewDate';
import {
  DocumentationDisplayedFile,
  GalleryFileRef,
  GallerySliceState,
  GalleryStateAfterRemoval,
  PinnedVersion,
} from './types';
import { BaseGallerySlice } from '../../../gallery/createGallerySlice';
import { DocumentationBaseIds } from 'shared/types/documentation';
import { ROOT_DIR_ID } from 'shared/constants/directory';
import { findById } from 'shared/utils/id/findById';
import { isNonEmptyArray } from 'shared/utils/array';
import type { DocumentationSignedUrls } from 'shared/domain/documentation/documentationContract';
import { ImageConvertStatus } from 'shared/domain/imageConvertStatus/imageConvertStatus';

export const INITIAL_GALLERY_STATE: Omit<GallerySliceState, keyof BaseGallerySlice<DocumentationBaseIds>> = {
  selectedFileIds: new Set(),
  gallerySourceDirectoryId: null,
  pinnedVersion: null,
};

export function isDocumentationRow(
  row: DocumentationViewItem
): row is DocumentationViewItem & DocumentationModel {
  return isFileItemType(row.type);
}

/**
 * The version the gallery shows for a documentation: the one the user opened
 * by a link (PT-7326), even an outdated or withdrawn one, otherwise the
 * displayed (current) one.
 */
export function resolveGalleryVersion(
  documentation: DocumentationModel,
  pinnedVersionId?: string
): VersionModel | undefined {
  return findVersionById(documentation, pinnedVersionId) ?? findDisplayedVersion(documentation);
}

/** The version of the documentation the user opened by a link; `undefined` for another documentation. */
export function pinnedVersionOf(
  pinned: PinnedVersion | null | undefined,
  documentationId: string | undefined
): string | undefined {
  return pinned && documentationId && areObjectIdsEqual(pinned.documentationId, documentationId)
    ? pinned.versionId
    : undefined;
}

/** A version stays pinned only while the preview is open: opened again, it shows the current version. */
export function keepPinWhileOpen(open: boolean, pinned: PinnedVersion | null): PinnedVersion | null {
  return open ? pinned : null;
}

export function mapRowToDisplayedFile(
  row: DocumentationViewItem,
  pinnedVersionId?: string
): DocumentationDisplayedFile | null {
  if (!isDocumentationRow(row)) return null;
  return mapDocumentationToDisplayedFile(row, pinnedVersionId);
}

export function mapDocumentationToDisplayedFile(
  doc: DocumentationModel,
  pinnedVersionId?: string
): DocumentationDisplayedFile {
  const displayedVersion = resolveGalleryVersion(doc, pinnedVersionId);

  const parentDirectoryId = doc.parentId == null ? ROOT_DIR_ID : doc.parentId;

  if (!displayedVersion?._id) {
    return {
      localId: doc.localId || 0,
      title: doc.name,
      description: doc.number,
      deleted: Boolean(doc.deleted),
      type: '',
      imageRotation: 0,
      imageConvertStatus: undefined,
      loading: false,
      data: { extension: '' },
      documentationId: doc._id || '',
      versionId: '',
      createdAt: stringToViewDate(doc.createdAt),
      createdBy: userWithPersonalDataToOptionalLabelled(doc.createdBy, null),
      sourceIssueId: doc.sourceIssue?.issueId,
      sourceIssueNumber: doc.sourceIssue?.issueNumber,
      parentDirectoryId,
    };
  }

  return {
    localId: doc.localId || 0,
    title: doc.name,
    description: doc.number,
    deleted: Boolean(doc.deleted),
    type: displayedVersion.type || '',
    imageRotation: displayedVersion.imageRotation || 0,
    imageConvertStatus: displayedVersion.imageConvertStatus,
    loading: true,
    data: {
      extension: displayedVersion.extension || '',
    },
    documentationId: doc._id || '',
    versionId: displayedVersion._id,
    createdAt: stringToViewDate(doc.createdAt),
    createdBy: userWithPersonalDataToOptionalLabelled(doc.createdBy, null),
    sourceIssueId: doc.sourceIssue?.issueId,
    sourceIssueNumber: doc.sourceIssue?.issueNumber,
    parentDirectoryId,
  };
}

/**
 * Stable identity of a gallery file: `documentationId` when the file is saved,
 * otherwise its `localId`. Use it to match the same file across list refreshes
 * (e.g. pinning the edited file). Unlike `findGalleryFileIndex`, it never falls
 * back to `localId` for a saved file - uploads can share a placeholder `localId`.
 */
export function getGalleryFileKey(file: GalleryFileRef): string | undefined {
  if (file.documentationId) {
    return file.documentationId;
  }
  return file.localId == null ? undefined : `local:${file.localId}`;
}

/**
 * Latest documentation model for a gallery file, looked up in `sources` in
 * order (e.g. current directory docs, then search results).
 */
export function findDocumentationForGalleryFile(
  file: GalleryFileRef,
  sources: readonly (DocumentationModel[] | undefined)[]
): DocumentationModel | undefined {
  if (!file.documentationId) {
    return undefined;
  }
  for (const source of sources) {
    const doc = findById(source, file.documentationId, 'findDocumentationForGalleryFile');
    if (doc) {
      return doc;
    }
  }
  return undefined;
}

export function findGalleryFileIndex(
  files: DocumentationDisplayedFile[],
  target: Pick<DocumentationDisplayedFile, 'documentationId' | 'localId'>
): number {
  if (target.documentationId) {
    const byDocId = files.findIndex(
      (file) => file.documentationId === target.documentationId
    );
    if (byDocId !== -1) {
      return byDocId;
    }
  }

  if (target.localId != null) {
    return files.findIndex((file) => file.localId === target.localId);
  }

  return -1;
}

/**
 * Keep the gallery on the same documentation after grid rows refresh the file list
 * (filters, search, directory reload, edit save).
 *
 * Returns null when the open file is missing from `nextFiles` — caller must keep
 * the previous gallery list instead of adopting an unrelated directory listing.
 */
export function resolveSlideAfterGalleryFilesUpdate(
  previousFiles: DocumentationDisplayedFile[],
  nextFiles: DocumentationDisplayedFile[],
  galleryOpen: boolean,
  currentSlide: number
): number | null {
  if (!galleryOpen) {
    return currentSlide;
  }

  if (nextFiles.length === 0) {
    return null;
  }

  const currentFile = previousFiles[currentSlide];
  if (!currentFile) {
    return Math.min(currentSlide, nextFiles.length - 1);
  }

  const nextIndex = findGalleryFileIndex(nextFiles, currentFile);
  if (nextIndex !== -1) {
    return nextIndex;
  }

  return null;
}

/**
 * Gallery state after a file was removed from the open gallery (archive/restore).
 *
 * The file has to be dropped explicitly: when the open file disappears from the
 * grid, `setGalleryFilesFromGridRows` keeps the previous list (see its `null`
 * branch), so the removed file would otherwise linger in the preview.
 *
 * Shows the next file, the previous one when the last file was removed, or
 * closes the gallery when nothing is left. `fallbackIndex` is used when the file
 * is no longer listed (e.g. the list was already refreshed).
 */
export function resolveGalleryAfterFileRemoval(
  files: DocumentationDisplayedFile[],
  removed: GalleryFileRef,
  fallbackIndex: number
): GalleryStateAfterRemoval {
  const removedIndex = findGalleryFileIndex(files, removed);
  const remainingFiles =
    removedIndex === -1
      ? files
      : files.filter((_, index) => index !== removedIndex);

  if (!isNonEmptyArray(remainingFiles)) {
    return { files: remainingFiles, open: false, slide: 0 };
  }

  const slide = Math.min(
    removedIndex === -1 ? fallbackIndex : removedIndex,
    remainingFiles.length - 1
  );
  return { files: remainingFiles, open: true, slide };
}

/**
 * When the grid re-syncs (filters, search, directory refresh), we must not drop
 * already-fetched signed URLs — otherwise the gallery stays on loading spinners.
 * Called from `setGalleryFilesFromGridRows` in the documentation gallery Zustand slice.
 */
export function mergeGalleryFilesPreservingSignedUrls(
  previousFiles: DocumentationDisplayedFile[],
  nextFiles: DocumentationDisplayedFile[]
): DocumentationDisplayedFile[] {
  // Index previous files once (first match wins, as before) - O(n + m) per sync.
  const previousByIdentity = new Map<string, DocumentationDisplayedFile>();
  previousFiles.forEach((file) => {
    const key = getGalleryFileIdentity(file);
    if (!previousByIdentity.has(key)) {
      previousByIdentity.set(key, file);
    }
  });

  return nextFiles.map((next) => {
    const prev = previousByIdentity.get(getGalleryFileIdentity(next));
    if (!prev) {
      return next;
    }

    const sameVersion = prev.versionId === next.versionId;

    // Keep current client-side rotation while the same version is being re-synced.
    // This prevents a visual flip back to stale backend state between async updates.
    const mergedBase: DocumentationDisplayedFile = {
      ...next,
      imageRotation: sameVersion ? prev.imageRotation : next.imageRotation,
    };

    if (!sameVersion || !prev.signedRequest) {
      return mergedBase;
    }

    return {
      ...mergedBase,
      signedRequest: prev.signedRequest,
      downloadSrc: prev.downloadSrc ?? next.downloadSrc,
      src: prev.src ?? next.src,
      thumbnail: prev.thumbnail ?? next.thumbnail,
      loading: false,
    };
  });
}

/** Same file across gallery syncs: same local id and documentation id. */
function getGalleryFileIdentity(file: GalleryFileRef): string {
  return `${file.localId ?? ''}|${file.documentationId ?? ''}`;
}

/**
 * Resolve the effective directory for gallery operations.
 * Priority: gallerySourceDirectoryId > open file's parentDirectoryId > fallback.
 */
export function resolveEffectiveGalleryDirectory(
  gallerySourceDirectoryId: string | null,
  openFileParentDirectoryId: string | undefined,
  fallbackDirectory: string
): string {
  return gallerySourceDirectoryId ?? openFileParentDirectoryId ?? fallbackDirectory;
}

/**
 * Same as `resolveEffectiveGalleryDirectory`, but pulls its inputs directly
 * from store state so callers don't each re-derive the same field lookups.
 */
export function getEffectiveGalleryDirectory(state: {
  gallerySourceDirectoryId: string | null;
  files: DocumentationDisplayedFile[];
  currentSlide: number;
  currentDirectory: string;
}): string {
  return resolveEffectiveGalleryDirectory(
    state.gallerySourceDirectoryId,
    state.files[state.currentSlide]?.parentDirectoryId,
    state.currentDirectory
  );
}

/**
 * Create a patched file with updated metadata from the documentation model.
 * Returns the original file if metadata is unchanged.
 */
export function createPatchedFileWithMetadata(
  file: DocumentationDisplayedFile,
  doc: DocumentationModel
): DocumentationDisplayedFile {
  if (doc.name === file.title && doc.number === file.description) {
    return file;
  }
  return { ...file, title: doc.name, description: doc.number };
}

/**
 * A gallery file once its version's signed URLs arrived: the displayed source
 * prefers the drawn, then converted image, the download keeps the original.
 */
export function applySignedUrlsToGalleryFile(
  file: DocumentationDisplayedFile,
  urls: DocumentationSignedUrls
): Partial<DocumentationDisplayedFile> {
  return {
    signedRequest: {
      signedRequest: urls.signedRequest,
      imageSignedRequest: urls.imageSignedRequest,
      thumbnailSignedRequest: urls.thumbnailSignedRequest,
      mergedSignedRequest: urls.mergedSignedRequest,
      mergedThumbnailSignedRequest: urls.mergedThumbnailSignedRequest,
      drawingSignedRequest: urls.drawingSignedRequest,
    },
    downloadSrc: urls.signedRequest,
    src:
      urls.mergedSignedRequest ||
      urls.drawingSignedRequest ||
      urls.imageSignedRequest ||
      urls.signedRequest,
    thumbnail:
      urls.mergedThumbnailSignedRequest ||
      urls.imageSignedRequest ||
      urls.thumbnailSignedRequest ||
      urls.signedRequest,
    imageConvertStatus: urls.imageSignedRequest ? ImageConvertStatus.SUCCESS : file.imageConvertStatus,
    loading: false,
    ...(urls.imageRotation !== undefined ? { imageRotation: urls.imageRotation } : {}),
  };
}
